import { SceneEditor } from '@zakeke/zakeke-3d-core';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DAMDialog from 'src/components/pages/preview3D/dialogs/damDialog';
import { UploadDialog } from 'src/components/pages/preview3D/dialogs/uploadDialog';
import useMeshSides from 'src/components/pages/preview3D/hooks/useMeshSide';
import usePreview3DValidation from 'src/components/pages/preview3D/hooks/usePreview3DValidation';
import DAMBanner from 'src/components/widgets/DAMBanner/DAMBanner';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import CloneIcon from '../icons/cloneIcon.svg';
import GroupIcon from '../icons/groupIcon.svg';
import ImportIcon from '../icons/importIcon.svg';
import RemoveIcon from '../icons/removeIcon.svg';
import RenameIcon from '../icons/renameIcon.svg';
import { ToolGrid } from '../toolsgrid';
import TreeComponent, { TreeHandle } from '../TreeComponent';
import ObjectOverlayComponent from './overlay/ObjectOverlayComponent';
import { useTreeNodeManager } from './hooks/useTreeNodeManager';

export interface SceneObjectNode {
    id: string;
    name: string;
    position: [number, number, number];
    rotation: [number, number, number];
    scaling: [number, number, number];
    scaleSlider: number;
    materialOptions: { value: string; label: string }[];
    selectedMaterial: { value: string; label: string };
    showInAR: boolean;
    enableInVTO: boolean;
    children: SceneObjectNode[];
    visible?: boolean;
    displayOrder?: number;
    metadata?: { [key: string]: any }; // Metadata personalizzati per il nodo
    data: any; // Per ObjectEditor conterrà la mesh di Babylon
    onNodeClick: () => void; // Handler per la selezione mesh quando si clicca sul nodo
}

interface ObjectEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    materials: any[];
    sceneEditor: SceneEditor;
    onMeshUpdate: (meshId: string, updates: Partial<SceneObjectNode>) => void;
    onMeshesUpdated: (meshes: any[]) => void;
    notifySceneEditorUpdates: () => void;
    onRefreshMeshVisibility?: (refreshFn: () => void) => void; // Callback per esporre la funzione di refresh
}

const ObjectEditor: FC<ObjectEditorProps> = ({ treeRef, onMeshUpdate, onMeshesUpdated, sceneEditor, notifySceneEditorUpdates, onRefreshMeshVisibility }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const preview3DStore = useContext(Preview3DStoreContext);
    const validate = usePreview3DValidation();
    const { createMeshSide } = useMeshSides();
    const [zkProduct, designManager, sceneManager, zkScene] = usePreview3DStore((x) => [x.zkProduct, x.designManager, x.sceneManager, x.zkScene]);

    // Stato per forzare il refresh delle mesh
    const [meshRefreshTrigger, setMeshRefreshTrigger] = useState(false);
    const [meshes, setMeshes] = useState<any[]>(() => {
        const initialMeshes = sceneEditor?.getSceneMeshes() || [];
        console.log('🚀 ObjectEditor: Initial meshes loaded:', {
            count: initialMeshes.length,
            sceneEditorExists: !!sceneEditor,
            meshNames: initialMeshes.map((m) => m.name)
        });
        return initialMeshes;
    });

    useEffect(() => {
        if (meshRefreshTrigger && sceneEditor) {
            const newMeshes = sceneEditor.getSceneMeshes();
            setMeshes(newMeshes);
            setMeshRefreshTrigger(false);
        }
    }, [meshRefreshTrigger, sceneEditor]);

    // Esponi la funzione di refresh tramite il callback
    useEffect(() => {
        if (onRefreshMeshVisibility) {
            const refreshFunction = () => {
                console.log('🔄 ObjectEditor: Forcing mesh refresh from external trigger');
                setMeshRefreshTrigger(true);
            };
            onRefreshMeshVisibility(refreshFunction);
        }
    }, [onRefreshMeshVisibility]);

    // Controlla periodicamente i cambiamenti nelle mesh per le VTO reference
    useEffect(() => {
        if (!sceneEditor) return;

        const checkMeshChanges = () => {
            const currentMeshes = sceneEditor.getSceneMeshes();
            const currentMeshCount = currentMeshes.length;
            const currentVTOMeshCount = currentMeshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Confronta con lo stato precedente
            const previousMeshCount = meshes.length;
            const previousVTOMeshCount = meshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Se il numero di mesh è cambiato o se le VTO reference sono cambiate
            if (currentMeshCount !== previousMeshCount || currentVTOMeshCount !== previousVTOMeshCount) {
                console.log('🔍 Mesh changes detected:', {
                    previousMeshCount,
                    currentMeshCount,
                    previousVTOMeshCount,
                    currentVTOMeshCount
                });
                setMeshRefreshTrigger(true);
            }
        };

        // Controlla ogni 500ms per cambiamenti
        const interval = setInterval(checkMeshChanges, 500);

        return () => clearInterval(interval);
    }, [sceneEditor, meshes]);

    const materials = useMemo(() => {
        if (!sceneEditor) return [];
        return sceneEditor.getAllMaterials();
    }, [sceneEditor]);

    // Funzione per costruire la gerarchia delle mesh
    const buildMeshHierarchy = useCallback(
        (meshes: any[]): SceneObjectNode[] => {
            if (!meshes || meshes.length === 0) {
                return [];
            }

            const meshMap = new Map<string, any>();
            const rootMeshes: any[] = [];

            // Prima passata: crea la mappa delle mesh
            meshes.forEach((mesh) => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;
                meshMap.set(meshId, mesh);
            });

            // Seconda passata: identifica le mesh root
            meshes.forEach((mesh) => {
                const hasParent = mesh.parent && meshMap.has(mesh.parent.uniqueId || mesh.parent.id);
                if (!hasParent) {
                    rootMeshes.push(mesh);
                }
            });

            // Funzione ricorsiva per convertire mesh in SceneObjectNode
            const convertMeshToNode = (mesh: any): SceneObjectNode => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;

                // Trova i figli di questa mesh
                const children = meshes.filter((m) => m.parent && (m.parent.uniqueId === meshId || m.parent.id === meshId)).map(convertMeshToNode);

                // Estrai i materiali disponibili dalla mesh
                const meshMaterials = materials.map((mat: any) => ({
                    value: mat.id || mat.name || 'default',
                    label: mat.name || 'Default Material'
                }));

                // Trova il materiale selezionato per questa mesh
                let selectedMaterial = meshMaterials[0];
                if (mesh.material) {
                    const found = meshMaterials.find((mat: any) => mat.value === mesh.material.id || mat.value === mesh.material.name);
                    if (found) {
                        selectedMaterial = found;
                    }
                }

                // Determina se è un nodo root (senza parent tra le mesh)
                const isRootNode = !mesh.parent || !meshes.some((m) => m.uniqueId === mesh.parent.uniqueId || m.id === mesh.parent.id);

                // Determina se è un nodo transform (contiene "transform" nel nome)
                const isTransformNode = (mesh.name || '').toLowerCase().includes('transform');

                // Inizializza metadata dal mesh esistente o crea nuovo
                const baseMetadata = mesh.metadata || {};

                // Aggiungi metadata automatici
                const metadata = {
                    ...baseMetadata,
                    displayOrder: baseMetadata.displayOrder || 0,
                    ...(isRootNode && { isRootNode: true }),
                    ...(isTransformNode && { isTransformNode: true })
                };

                // Determina la visibilità del nodo basandosi sul parent se presente
                let meshVisibility: boolean;

                if (mesh.isVisible === false) {
                    // Se la mesh è esplicitamente false, forza a false
                    meshVisibility = false;
                    console.log(`❌ FORCED FALSE for "${mesh.name}" because mesh.isVisible === false`);
                } else if (mesh.parent && mesh.parent.isVisible !== undefined) {
                    // Se ha un parent, usa la visibilità del parent
                    meshVisibility = mesh.parent.isVisible !== false;
                    console.log(`👁️ Using parent visibility for "${mesh.name}":`, {
                        parentName: mesh.parent.name,
                        parentVisible: mesh.parent.isVisible,
                        calculatedVisibility: meshVisibility
                    });
                } else {
                    // Se non ha parent, usa la visibilità della mesh stessa
                    meshVisibility = mesh.isVisible !== false;
                    console.log(`👁️ Using mesh visibility for "${mesh.name}":`, {
                        meshVisible: mesh.isVisible,
                        calculatedVisibility: meshVisibility,
                        reason: !mesh.parent ? 'No parent' : 'Parent isVisible undefined'
                    });
                }

                const node: SceneObjectNode = {
                    id: meshId,
                    name: mesh.name || 'Unnamed Mesh',
                    position: mesh.position ? [mesh.position.x, mesh.position.y, mesh.position.z] : [0, 0, 0],
                    rotation: mesh.rotation ? [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z] : [0, 0, 0],
                    scaling: mesh.scaling ? [mesh.scaling.x, mesh.scaling.y, mesh.scaling.z] : [1, 1, 1],
                    scaleSlider: mesh.scaling ? Math.max(mesh.scaling.x, mesh.scaling.y, mesh.scaling.z) : 1,
                    materialOptions: meshMaterials,
                    selectedMaterial,
                    showInAR: sceneEditor.isAREnabledForMesh(mesh.id) || true,
                    enableInVTO: sceneEditor.isTryOnEnabledForMesh(mesh.id) || false,
                    children,
                    data: mesh,
                    visible: meshVisibility,
                    displayOrder: metadata.displayOrder,
                    metadata, // Aggiungi metadata al nodo
                    // Handler per la selezione mesh quando si clicca sul nodo
                    onNodeClick: () => {
                        console.log('🎯 Node clicked, selecting mesh:', mesh.name, 'ID:', meshId);

                        if (!sceneEditor) {
                            console.warn('⚠️ SceneEditor not available');
                            return;
                        }

                        try {
                            let targetMesh = mesh;
                            const targetMeshId = targetMesh.id || targetMesh.uniqueId;

                            // Attiva l'highlight
                            if (sceneEditor.setHighlightOver) {
                                sceneEditor.setHighlightOver(true);
                            }

                            // Seleziona la mesh
                            if (sceneEditor.selectMesh) {
                                sceneEditor.selectMesh(targetMeshId);
                                console.log('✅ Selected mesh:', targetMeshId, 'in SceneEditor');
                            }

                            // Attiva la modalità selezione se non è già attiva
                            if (sceneEditor.isSelectionMode !== undefined && !sceneEditor.isSelectionMode) {
                                sceneEditor.isSelectionMode = true;
                                console.log('🔄 Enabled selection mode');
                            }
                        } catch (error) {
                            console.error('❌ Error selecting mesh from node click:', error);
                        }
                    }
                };

                // Sincronizza anche il metadata sulla mesh di Babylon
                if (!mesh.metadata) mesh.metadata = {};
                Object.assign(mesh.metadata, metadata);

                if (isRootNode || isTransformNode) {
                    console.log(`🏷️  Node "${node.name}" metadata applied:`, {
                        isRootNode: isRootNode || undefined,
                        isTransformNode: isTransformNode || undefined,
                        allMetadata: metadata
                    });
                }

                return node;
            };

            return rootMeshes.map(convertMeshToNode);
        },
        [materials, sceneEditor, meshes]
    );

    // Converti le mesh in SceneObjectNode con logging di riepilogo
    const initialObjects: SceneObjectNode[] = useMemo(() => {
        console.log('🔄 ObjectEditor: Processing mesh hierarchy...');
        console.log('📊 Available meshes:', {
            meshesLength: meshes.length,
            sceneEditorExists: !!sceneEditor,
            firstMesh: meshes[0]?.name || 'No meshes'
        });

        if (!meshes || meshes.length === 0) {
            console.log('❌ ObjectEditor: No meshes available for metadata processing');
            return [];
        }

        console.log('🔄 Processing mesh hierarchy with metadata...');
        const result = buildMeshHierarchy(meshes);

        // Riepilogo metadata applicati
        const countMetadata = (nodes: SceneObjectNode[]): { root: number; transform: number; total: number } => {
            let root = 0,
                transform = 0,
                total = 0;

            const count = (nodeList: SceneObjectNode[]) => {
                nodeList.forEach((node) => {
                    total++;
                    if (node.metadata?.isRootNode) root++;
                    if (node.metadata?.isTransformNode) transform++;
                    if (node.children?.length) count(node.children);
                });
            };

            count(nodes);
            return { root, transform, total };
        };

        const stats = countMetadata(result);
        console.log(`📊 Metadata Summary: ${stats.total} total nodes, ${stats.root} root nodes, ${stats.transform} transform nodes`);

        return result;
    }, [meshes, buildMeshHierarchy]);

    const syncNodeToMeshRef = useRef<(node: SceneObjectNode) => void>();

    useEffect(() => {
        syncNodeToMeshRef.current = (node: SceneObjectNode) => {
            if (!node.data) return;

            const mesh = node.data;

            // Sincronizza nome
            mesh.name = node.name;

            // Sincronizza posizione
            if (mesh.position) {
                mesh.position.x = node.position[0];
                mesh.position.y = node.position[1];
                mesh.position.z = node.position[2];
            }

            // Sincronizza rotazione
            if (mesh.rotation) {
                mesh.rotation.x = node.rotation[0];
                mesh.rotation.y = node.rotation[1];
                mesh.rotation.z = node.rotation[2];
            }

            // Sincronizza scala
            if (mesh.scaling) {
                mesh.scaling.x = node.scaling[0];
                mesh.scaling.y = node.scaling[1];
                mesh.scaling.z = node.scaling[2];
            }

            // Sincronizza materiale
            if (node.selectedMaterial && sceneEditor) {
                try {
                    const material = materials.find((mat) => mat.id === node.selectedMaterial.value || mat.name === node.selectedMaterial.value);
                    if (material && mesh.material !== material) {
                        mesh.material = material;
                    }
                } catch (error) {
                    console.error('Error applying material:', error);
                }
            }

            // Sincronizza visibilità
            if (mesh.setEnabled) {
                mesh.setEnabled(node.visible !== false);
            } else if (mesh.isVisible !== undefined) {
                mesh.isVisible = node.visible !== false;
            }

            // Sincronizza metadata
            if (!mesh.metadata) mesh.metadata = {};
            mesh.metadata.showInAR = node.showInAR;
            mesh.metadata.enableInVTO = node.enableInVTO;
            mesh.metadata.displayOrder = node.displayOrder;

            // Sincronizza metadata personalizzati
            if (node.metadata) {
                Object.assign(mesh.metadata, node.metadata);
            }

            if (onMeshUpdate) {
                onMeshUpdate(node.id, node);
            }
        };
    }, [onMeshUpdate, materials, sceneEditor]);

    const createObjectOverlayById = useCallback(
        (nodeId: string): (() => JSX.Element) => {
            return () => {
                // Trova il nodo aggiornato al momento del render dell'overlay
                const { findNodeById } = treeNodeManagerRef.current || {};
                const node = findNodeById ? findNodeById(nodeId) : null;

                if (!node) {
                    console.error('Nodo non trovato per overlay:', nodeId);
                    return <div>Node not found</div>;
                }

                return (
                    <ObjectOverlayComponent
                        key={`overlay-component-${node.id}`}
                        node={node}
                        sceneEditor={sceneEditor}
                        onPropertiesUpdate={(updates) => {
                            const { updateNode, findNodeById } = treeNodeManagerRef.current || {};

                            if (!updateNode || !findNodeById) return;

                            // Aggiorna il nodo nel tree
                            updateNode(node.id, updates);

                            // Trova il nodo aggiornato e sincronizza con Babylon
                            const updatedNode = findNodeById(node.id);
                            if (updatedNode && syncNodeToMeshRef.current) {
                                const nodeWithUpdates = { ...updatedNode, ...updates };
                                syncNodeToMeshRef.current(nodeWithUpdates);
                            }

                            // Gestione speciale per i materiali
                            if ('selectedMaterial' in updates && sceneEditor) {
                                const material = sceneEditor.getMaterialByID(updates.selectedMaterial!.value);
                                if (material) {
                                    // Funzione ricorsiva per applicare il materiale a mesh e figli
                                    const applyMaterialRecursively = (meshNode: SceneObjectNode) => {
                                        sceneEditor.applyMaterial(material, meshNode.data);
                                        meshNode.children?.forEach(applyMaterialRecursively);
                                    };

                                    const currentNodeForMaterial = findNodeById(node.id);
                                    if (currentNodeForMaterial) {
                                        applyMaterialRecursively(currentNodeForMaterial);
                                    }
                                }

                                // Aggiorna anche i nodi figli con il nuovo selectedMaterial
                                const updateChildrenMaterial = (meshNode: SceneObjectNode) => {
                                    meshNode.children?.forEach((child) => {
                                        updateNode(child.id, { selectedMaterial: updates.selectedMaterial });
                                        updateChildrenMaterial(child);
                                    });
                                };

                                const currentNodeForChildren = findNodeById(node.id);
                                if (currentNodeForChildren) {
                                    updateChildrenMaterial(currentNodeForChildren);
                                }
                            }
                        }}
                        onPropertyChanged={(property, value) => {
                            console.log(`🔄 Property ${property} changed to:`, value);
                            // Notifica gli aggiornamenti del scene editor se necessario
                            if (notifySceneEditorUpdates) {
                                notifySceneEditorUpdates();
                            }
                        }}
                    />
                );
            };
        },
        [meshes, materials]
    );

    // Wrapper per compatibilità con l'hook che si aspetta (node: T) => () => React.ReactNode
    const createObjectOverlay = useCallback(
        (node: SceneObjectNode): (() => JSX.Element) => {
            // Semplicemente delega alla versione che prende l'ID
            return createObjectOverlayById(node.id);
        },
        [createObjectOverlayById]
    );

    // Ref per memorizzare le funzioni dell'hook dopo l'inizializzazione
    const treeNodeManagerRef = useRef<{
        updateNode: (id: string, updates: Partial<SceneObjectNode>) => void;
        findNodeById: (id: string) => SceneObjectNode | undefined;
    }>();

    // Usa l'hook per la gestione dei nodi con il createObjectOverlay già definito
    const {
        filter,
        setFilter,
        selectedIds,
        filteredData,
        updateNode,
        findNodeById,
        removeNodes,
        cloneSelectedNodes,
        groupSelectedNodes,
        treeState,
        syncInitialVisibilityFromMeshes
    } = useTreeNodeManager<SceneObjectNode>(initialObjects, createObjectOverlay);

    // Memorizza le funzioni nell'ref per l'uso negli overlay
    useEffect(() => {
        treeNodeManagerRef.current = {
            updateNode,
            findNodeById
        };
    }, [updateNode, findNodeById]);

    // 🔧 NUOVO: Sincronizza la visibilità iniziale delle mesh solo UNA volta quando i nodi sono pronti
    const [initialSyncDone, setInitialSyncDone] = useState(false);

    useEffect(() => {
        if (initialObjects.length > 0 && !initialSyncDone) {
            console.log('🔄 Syncing initial mesh visibility from Babylon.js (one-time only)...');
            syncInitialVisibilityFromMeshes();
            setInitialSyncDone(true);
        }
    }, [initialObjects, syncInitialVisibilityFromMeshes, initialSyncDone]);

    const prepareModel = (meshes: BABYLON.AbstractMesh[]) => {
        const { amountOfSides, amountOfMeshesWithGeometry } = validate();

        if (amountOfSides === 1 && amountOfMeshesWithGeometry === 1) {
            const firstMeshWithGeometry = sceneEditor!.scene!.meshes.find((x) => x.getTotalIndices() > 0);
            if (firstMeshWithGeometry) {
                const meshSide = createMeshSide(zkProduct!.get('colors').at(0).get('colorID'), zkProduct!.get('colors').at(0).get('sides').at(0).get('sideID'));
                meshSide.set('meshID', firstMeshWithGeometry.id);
            }
        }

        // Aggiorna le mesh nell'ObjectEditor dopo l'upload
        if (sceneEditor) {
            setMeshRefreshTrigger(true);
            const updatedMeshes = sceneEditor.getSceneMeshes();
            onMeshesUpdated(updatedMeshes);
        }
    };

    const handleUploadClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.obj,.gltf,.glb,.babylon,.zip';

        input.onchange = (e) => {
            document.body.removeChild(input);
            const files = (e.target as HTMLInputElement).files;
            if (files) {
                showDialog(
                    'upload-file',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <UploadDialog
                            files={files}
                            onComplete={(meshes) => {
                                prepareModel(meshes);
                                closeDialog('upload-file');
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
            }
        };

        input.oncancel = () => {
            document.body.removeChild(input);
        };

        input.style.display = 'none';
        input.click();
        document.body.appendChild(input);
    };

    const handleUploadFromDAMClick = () => {
        showDialog(
            'dam-dialog',
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <DAMDialog
                    onClose={(productAssetUrl: string) => {
                        closeDialog('dam-dialog');
                        showDialog(
                            'upload-file',
                            <Preview3DStoreContext.Provider value={preview3DStore}>
                                <UploadDialog
                                    damProductAssetUrl={productAssetUrl}
                                    onComplete={(meshes) => {
                                        prepareModel(meshes);
                                        closeDialog('upload-file');
                                    }}
                                />
                            </Preview3DStoreContext.Provider>
                        );
                    }}
                />
            </Preview3DStoreContext.Provider>
        );
    };

    const handleToggleNodeVisibility = useCallback(
        (meshId: string, currentVisibility: boolean) => {
            if (!sceneEditor) return;

            const newVisibility = !currentVisibility;
            console.log(`🔄 Toggling visibility for mesh ${meshId}: ${currentVisibility} → ${newVisibility}`);

            // 1. Aggiorna la mesh in Babylon
            sceneEditor.showHideMesh(meshId, newVisibility, false);

            // 2. Trova il nodo corrispondente nell'albero
            const targetNode = findNodeById(meshId);
            if (targetNode) {
                console.log(`📝 Updating tree node "${targetNode.name}" visibility to: ${newVisibility}`);

                // 3. Aggiorna il nodo nell'albero (sia dati che UI state)
                updateNode(meshId, { visible: newVisibility });

                // 🔧 SEMPLIFICATO: Con la logica gerarchica, aggiorna solo il nodo corrente
                if (currentVisibility !== newVisibility) {
                    // Solo se la visibilità è effettivamente cambiata
                    treeState.toggleVisibility(meshId);
                }

                // 4. Se il nodo viene reso visibile, assicurati che i children siano visibili anche in Babylon
                if (newVisibility && targetNode.children && targetNode.children.length > 0) {
                    console.log(`👶 Making children visible in Babylon for parent: ${targetNode.name}`);

                    const updateChildrenMeshVisibility = (children: SceneObjectNode[]) => {
                        children.forEach((child) => {
                            console.log(`  └─ Making child mesh "${child.name}" visible in Babylon`);

                            // Aggiorna solo la mesh del child in Babylon (il tree si aggiorna automaticamente)
                            if (child.data?.id) {
                                sceneEditor.showHideMesh(child.data.id, true, false);
                            }

                            // Ricorsivamente per i nipoti
                            if (child.children && child.children.length > 0) {
                                updateChildrenMeshVisibility(child.children);
                            }
                        });
                    };

                    updateChildrenMeshVisibility(targetNode.children);
                }
                console.log(`✅ Visibility toggle completed for ${meshId}`);
            } else {
                console.warn(`⚠️ Node with id ${meshId} not found in tree`);
            }
        },
        [sceneEditor, findNodeById, updateNode, treeState]
    );

    // Trasforma i nodi in formato compatibile con TreeComponent
    const transformNodesForTree = useCallback(
        (nodes: SceneObjectNode[]): any[] => {
            if (!nodes || nodes.length === 0) {
                return [];
            }

            return nodes.map((node) => ({
                id: node.id,
                name: node.name,
                visible: node.visible,
                displayOrder: node.displayOrder,
                children: node.children ? transformNodesForTree(node.children) : [],
                component: createObjectOverlay(node), // Usa il wrapper che prende il nodo
                doNotOpenOverlay: node.name && node.name.toLowerCase().includes('vto_reference'), // Don't open overlay for VTO reference meshes
                customIcon: node.name && node.name.toLowerCase().includes('vto_reference') ? '🎭' : undefined, // Custom icon for VTO reference meshes
                data: node.data,
                onNodeClick: node.onNodeClick // ✅ AGGIUNTO: Passa l'onNodeClick al nodo trasformato
            }));
        },
        [createObjectOverlay]
    );

    // 5. Ottimizza la memoizzazione dei treeNodes
    const treeNodes = useMemo(() => {
        // Usa filteredData se c'è un filtro, altrimenti usa treeState.nodes
        const sourceNodes = filter ? filteredData : treeState.nodes;
        console.log('Rebuilding treeNodes from', filter ? 'filteredData' : 'treeState.nodes', ':', sourceNodes.length);
        const result = transformNodesForTree(sourceNodes);
        console.log('treeNodes rebuilt with', result.length, 'nodes');
        return result;
    }, [treeState.nodes, filteredData, filter, transformNodesForTree]);

    // ✅ NUOVO: Funzione per trovare un nodo basandosi sui dati della mesh
    const findNodeByDataID = useCallback((nodes: SceneObjectNode[], dataID: string): SceneObjectNode | null => {
        for (const node of nodes) {
            // Controlla se questo nodo corrisponde
            if (node.data?.uniqueId === dataID || node.data?.id === dataID || node.id === dataID) {
                return node;
            }
            // Cerca ricorsivamente nei figli
            if (node.children && node.children.length > 0) {
                const found = findNodeByDataID(node.children, dataID);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }, []);

    // Override del treeState per fornire i nodi trasformati
    const treeStateForComponent = useMemo(() => {
        const result = {
            ...treeState,
            nodes: treeNodes,
            findNodeByDataID: (nodes: any[], dataID: string) => findNodeByDataID(nodes, dataID)
        };
        return result;
    }, [treeState, treeNodes, findNodeByDataID]);

    // Struttura dati standard per nuovi oggetti
    const createNewObject = useCallback(
        (name: string): SceneObjectNode => ({
            id: `object-${Date.now()}`,
            name,
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scaling: [1, 1, 1],
            scaleSlider: 1,
            materialOptions: [{ value: 'Default', label: 'Default Material' }],
            selectedMaterial: { value: 'Default', label: 'Default Material' },
            showInAR: false,
            enableInVTO: false,
            children: [],
            visible: true,
            displayOrder: 0,
            metadata: {},
            data: undefined,
            onNodeClick: () => {
                console.log('sono node appena cliccato', name);
            }
        }),
        []
    );

    const tools = [
        {
            name: 'IMPORT',
            tooltip: 'Importa oggetti',
            icon: <ImportIcon />,
            onClick: () => {
                treeState.closeOverlay();
                showDialog(
                    'import-dialog',
                    <Dialog
                        title='Model Upload source'
                        buttons={[
                            {
                                label: 'Upload 3D model',
                                onClick: () => {
                                    closeDialog('import-dialog');
                                    handleUploadClick();
                                }
                            },
                            {
                                label: 'Import from Asset Library',
                                onClick: () => {
                                    closeDialog('import-dialog');
                                    handleUploadFromDAMClick();
                                }
                            }
                        ]}
                    >
                        <p>From where do you want to import the 3d model?</p>
                        <DAMBanner />
                    </Dialog>
                );
            }
        },
        {
            name: 'GROUP',
            tooltip: 'Crea nuova cartella',
            icon: <GroupIcon />,
            onClick: () => {
                if (!selectedIds.length) return;

                treeState.closeOverlay();
                let formData = { name: '' };

                showDialog(
                    'group-dialog',
                    <Dialog
                        title='New Group'
                        buttons={[
                            {
                                label: 'OK',
                                onClick: () => {
                                    const val = formData.name.trim();
                                    if (!val) return;
                                    groupSelectedNodes(val, createNewObject, (nodeId: string) => treeState.findParentId(nodeId));
                                    closeDialog('group-dialog');
                                }
                            }
                        ]}
                    >
                        <FormControl label='Group Name'>
                            <Input
                                placeholder='type group name...'
                                fullWidth
                                onChange={(e) => {
                                    formData.name = e.target.value;
                                }}
                            />
                        </FormControl>
                    </Dialog>
                );
            }
        },
        {
            name: 'CLONE',
            tooltip: 'Clona elementi selezionati',
            icon: <CloneIcon />,
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                // Raccogli i nodi da clonare
                const nodesToClone = Array.from(selectedIds)
                    .map((id) => findNodeById(id))
                    .filter(Boolean);

                // Clona prima le mesh di Babylon
                const clonedMeshes = new Map<string, any>();
                nodesToClone.forEach((node) => {
                    if (node && node.data && sceneEditor) {
                        try {
                            const clonedMesh = sceneEditor.cloneMesh(node.data.id || node.data.uniqueId);
                            clonedMeshes.set(node.id, clonedMesh);
                        } catch (error) {
                            console.error(`Errore clonando mesh per nodo ${node.name}:`, error);
                        }
                    }
                });

                // Ora clona i nodi dell'albero e assegna le mesh clonate
                cloneSelectedNodes('Copy', (node: SceneObjectNode) => {
                    const clonedMesh = clonedMeshes.get(node.id);
                    return clonedMesh;
                });

                // Dopo la clonazione, aggiorna la lista delle mesh per includere quelle nuove
                if (sceneEditor) {
                    setMeshRefreshTrigger(true);
                    const updatedMeshes = sceneEditor.getSceneMeshes();
                    onMeshesUpdated(updatedMeshes);
                }

                // Notifica gli aggiornamenti una sola volta
                notifySceneEditorUpdates();
            }
        },
        {
            name: 'RENAME',
            tooltip: 'Rinomina elemento selezionato',
            icon: <RenameIcon />,
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();
                const idToRename = selectedIds[0];
                const currNode = findNodeById(idToRename);

                if (!currNode) return;

                let formData = { name: currNode.name };

                showDialog(
                    'rename-dialog',
                    <Dialog
                        title='Rename Object'
                        buttons={[
                            {
                                label: 'OK',
                                onClick: () => {
                                    const val = formData.name.trim();
                                    if (val) {
                                        updateNode(idToRename, { name: val });
                                        // Sincronizza con la mesh di Babylon
                                        const updatedNode = findNodeById(idToRename);
                                        if (updatedNode && updatedNode.data) {
                                            updatedNode.data.name = val;
                                        }
                                        closeDialog('rename-dialog');
                                    }
                                }
                            }
                        ]}
                    >
                        <FormControl label='Object Name'>
                            <Input
                                defaultValue={currNode.name}
                                fullWidth
                                onChange={(e) => {
                                    formData.name = e.target.value;
                                }}
                            />
                        </FormControl>
                    </Dialog>
                );
            }
        },
        {
            name: 'REMOVE',
            tooltip: 'Rimuovi elemento selezionato',
            icon: <RemoveIcon />,
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                showDialog(
                    'remove-dialog',
                    <Dialog
                        title='Remove Object'
                        buttons={[
                            {
                                label: 'Cancel',
                                onClick: () => closeDialog('remove-dialog')
                            },
                            {
                                label: 'Remove',
                                onClick: () => {
                                    // Fai dispose delle mesh prima di rimuovere i nodi
                                    selectedIds.forEach((id) => {
                                        const node = findNodeById(id);

                                        if (node && node.data) {
                                            if (typeof node.data.dispose === 'function') {
                                                try {
                                                    node.data.dispose();
                                                    console.log('Mesh disposed:', node.data.name);
                                                } catch (error) {
                                                    console.error('Error disposing mesh:', error);
                                                }
                                            }
                                        }
                                    }); // Rimuovi i nodi dall'albero
                                    removeNodes(selectedIds);
                                    onMeshesUpdated(sceneEditor.getSceneMeshes());
                                    closeDialog('remove-dialog');
                                }
                            }
                        ]}
                    >
                        <p>Confirm removal?</p>
                    </Dialog>
                );
            }
        }
    ];

    return (
        <div>
            <ToolGrid tools={tools} columns={tools.length} />
            <TreeComponent
                ref={treeRef}
                treeState={treeStateForComponent}
                showVisibilityIcon={true}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Aggiorna la gerarchia in Babylon
                    nodesToMove.forEach((id) => {
                        const node = findNodeById(id);
                        if (node && node.data) {
                            const targetNode = targetParentId ? findNodeById(targetParentId) : null;
                            const newParent = targetNode?.data || null; // Usa data anche per il target

                            // Aggiorna il parent nella mesh di Babylon
                            if (node.data.setParent) {
                                node.data.setParent(newParent);
                            } else if (node.data.parent !== undefined) {
                                node.data.parent = newParent;
                            }
                        }
                    });

                    treeState.moveSelection(nodesToMove, targetParentId);
                }}
                filter={filter}
                setFilter={setFilter}
                onToggleNodeVisibility={handleToggleNodeVisibility}
            />
        </div>
    );
};

export default ObjectEditor;
